import React, { useState } from "react"
import { Pane } from "components/ui"
import { Box, StackView } from "@planningcenter/tapestry-react"
import { HelperShelfMessage } from "components/helper_shelf"
import { arrayOf, shape, string, func, bool, object } from "prop-types"
import { StatusMessage } from "components/ui"
import { api } from "utils/api"
import DraggableQuestions from "../DraggableQuestions"
import BlankFormQuestion from "../BlankFormQuestion"
import BlankQuestion from "../BlankQuestion"
import Footer from "./Footer/Footer"
import HeaderButtons from "./HeaderButtons"
import Title from "./Title"
import { reOrder } from "utils/arrays"

const REGISTRANT_QUESTION_LIMIT = 5

let dragTimeout

const QuestionList = ({
  questions: questionFromProps = [],
  navigate,
  basePath,
  questionOrderPath,
  blankStateImageURL,
  selectionsUrl,
  formsUrl,
  attendeeQuestionsUrl,
  group,
  signup,
  isActingStaff,
  onReorder = () => {}
}) => {
  const [questions, setQuestions] = useState(questionFromProps)
  const [saveStatus, setSaveStatus] = useState(null)
  const isFormQuestion = group === "attendee_form"
  const isAttendeeOrRegistrantQuestion = group === "registrant" || group === "attendee"
  const navigationPathNew = `${basePath}/new`

  const handleDragEnd = ({ source, destination }) => {
    if (!destination || destination.index === source.index) return

    setSaveStatus({
      text: "Saving...",
      icon: "spinner"
    })

    const originalQuestions = questions
    const newQuestions = reOrder(questions, source.index, destination.index)
    setQuestions(newQuestions)

    if (dragTimeout) clearTimeout(dragTimeout)

    dragTimeout = setTimeout(
      () =>
        api
          .put(questionOrderPath, {
            order: newQuestions.map(x => x.id),
            group: group
          })
          .then(resp => {
            setQuestions(resp.questions)
            onReorder(resp.questions)
            setSaveStatus({
              text: "Saved!",
              icon: "general#check",
              type: "success"
            })
            setTimeout(() => setSaveStatus({}), 3000)
          })
          .catch(_ => setQuestions(originalQuestions)),
      1500
    )
  }

  const handleQuestionTypeToggle = question => {
    setQuestions(currentQuestions => {
      return currentQuestions.filter(currentQuestion => currentQuestion.id !== question.id)
    })
  }

  return (
    <>
      {questions.length === 0 && isFormQuestion ? (
        <BlankFormQuestion
          selectionsUrl={selectionsUrl}
          navigationPathNew={navigationPathNew}
          navigate={navigate}
        />
      ) : (
        <>
          {questions.length === 0 && isAttendeeOrRegistrantQuestion ? (
            <BlankQuestion
              group={group}
              navigate={navigate}
              navigationPathNew={navigationPathNew}
              selectionsUrl={selectionsUrl}
              attendeeQuestionsUrl={attendeeQuestionsUrl}
              blankStateImageURL={blankStateImageURL}
              isSimpleEvent={signup?.isSimpleEvent}
            />
          ) : (
            <Pane>
              <Pane.Header axis="horizontal" distribution="space-between" alignment="center" wrap>
                <Title
                  group={group}
                  isFormQuestion={isFormQuestion}
                  questionsLength={questions.length}
                />

                <StackView axis="horizontal" alignment="center" distribution="flex-end">
                  {saveStatus && <StatusMessage {...saveStatus} style={{ marginRight: "1rem" }} />}
                  {questions.length > 0 && (
                    <HeaderButtons
                      navigationPathNew={navigationPathNew}
                      enableAddSection={isFormQuestion}
                      limit={REGISTRANT_QUESTION_LIMIT}
                      limitMet={
                        isAttendeeOrRegistrantQuestion &&
                        questions.length >= REGISTRANT_QUESTION_LIMIT
                      }
                      navigate={navigate}
                    />
                  )}
                </StackView>

                {isFormQuestion && (
                  <Box width="100%">
                    <HelperShelfMessage id={`questions-heading-${group}`}>
                      You can also specify profile information to collect on this form for each
                      attendee in the <a href={selectionsUrl}>selection type settings</a>
                    </HelperShelfMessage>
                  </Box>
                )}
              </Pane.Header>

              {questions.length > 0 && (
                <>
                  <DraggableQuestions
                    questions={questions}
                    basePath={basePath}
                    questionOrderPath={questionOrderPath}
                    group={group}
                    handleDragEnd={handleDragEnd}
                    signup={signup}
                    isActingStaff={isActingStaff}
                    onQuestionTypeToggle={handleQuestionTypeToggle}
                  />
                  {isAttendeeOrRegistrantQuestion && (
                    <Footer
                      group={group}
                      formsUrl={formsUrl}
                      attendeeQuestionsUrl={attendeeQuestionsUrl}
                      limit={REGISTRANT_QUESTION_LIMIT}
                      isSimpleEvent={signup?.isSimpleEvent}
                    />
                  )}
                </>
              )}
            </Pane>
          )}
        </>
      )}
    </>
  )
}

QuestionList.propTypes = {
  group: string.isRequired,
  questions: arrayOf(object),
  basePath: string,
  blankStateImageURL: string,
  questionOrderPath: string,
  navigate: func,
  signup: shape({ isSimpleEvent: bool }),
  selectionsUrl: string,
  formsUrl: string,
  attendeeQuestionsUrl: string,
  onReorder: func,
  isActingStaff: bool
}

export default QuestionList
